import type { AiSdkUiToolPart } from './ai-sdk-ui-messages';
import { uiToolPartSchema } from './ai-sdk-ui-messages';
import { ALINE_TOOL_NAMES } from './tool-calls';
import { parseConnectExternalAccountInput } from '../composio/connect-external-account';
import { parseEnableExternalToolsInput } from '../composio/enable-external-tools';

export type PendingConnectorRequest =
  | {
      kind: 'connect';
      nodeId: number | undefined;
      toolCallId: string;
      toolkit: string;
    }
  | {
      kind: 'enable';
      nodeId: number | undefined;
      toolCallId: string;
      toolkit: string;
      toolSlugs: readonly string[];
    };

export type ConnectorCardQueueItem =
  | {
      kind: 'connect';
      toolCallIds: readonly string[];
      toolkit: string;
    }
  | {
      kind: 'enable';
      requests: readonly { toolCallId: string; toolSlugs: readonly string[] }[];
      toolkit: string;
      unionSlugs: readonly string[];
    };

/**
 * Unresolved connect/enable pauses, in persisted part order. Malformed
 * inputs are skipped so a bad sibling cannot hide the rest of the queue.
 */
export function listPendingConnectorRequests(
  uiParts: unknown[] | null | undefined
): PendingConnectorRequest[] {
  const requests: PendingConnectorRequest[] = [];
  for (const part of parseUiToolParts(uiParts)) {
    if (part.state !== 'input-available') continue;

    const toolName = getToolPartName(part);
    if (toolName === ALINE_TOOL_NAMES.connectExternalAccount) {
      const parsedInput = parseConnectExternalAccountInput(part.input);
      if (!parsedInput) continue;

      requests.push({
        kind: 'connect',
        nodeId: part.nodeId,
        toolCallId: part.toolCallId,
        toolkit: parsedInput.toolkit,
      });
      continue;
    }
    if (toolName === ALINE_TOOL_NAMES.enableExternalTools) {
      const parsedInput = parseEnableExternalToolsInput(part.input);
      if (!parsedInput) continue;

      requests.push({
        kind: 'enable',
        nodeId: part.nodeId,
        toolCallId: part.toolCallId,
        toolSlugs: parsedInput.toolSlugs,
        toolkit: parsedInput.toolkit,
      });
    }
  }

  return requests;
}

/**
 * Group duplicate connection requests by toolkit and overlapping enable
 * lists by toolkit. Connection groups come first, then enable groups, each
 * in the order the first matching part appeared.
 */
export function getConnectorCardQueue(
  uiParts: unknown[] | null | undefined
): ConnectorCardQueueItem[] {
  const enableOrder: string[] = [];
  const connectOrder: string[] = [];
  const connectIds = new Map<string, string[]>();
  const pending = listPendingConnectorRequests(uiParts);
  const enableRequests = new Map<string, { toolCallId: string; toolSlugs: readonly string[] }[]>();

  for (const request of pending) {
    if (request.kind === 'connect') {
      const ids = connectIds.get(request.toolkit);
      if (!ids) {
        connectOrder.push(request.toolkit);
        connectIds.set(request.toolkit, [request.toolCallId]);
      } else ids.push(request.toolCallId);
      continue;
    }

    const grouped = enableRequests.get(request.toolkit);
    if (!grouped) {
      enableOrder.push(request.toolkit);
      enableRequests.set(request.toolkit, [
        { toolCallId: request.toolCallId, toolSlugs: request.toolSlugs },
      ]);
    } else grouped.push({ toolCallId: request.toolCallId, toolSlugs: request.toolSlugs });
  }

  return [
    ...connectOrder.map(toolkit => ({
      kind: 'connect' as const,
      toolCallIds: connectIds.get(toolkit) ?? [],
      toolkit,
    })),
    ...enableOrder.map(toolkit => {
      const requests = enableRequests.get(toolkit) ?? [];
      return {
        kind: 'enable' as const,
        requests,
        toolkit,
        unionSlugs: uniqueSorted(requests.flatMap(request => [...request.toolSlugs])),
      };
    }),
  ];
}

function parseUiToolParts(uiParts: unknown[] | null | undefined): AiSdkUiToolPart[] {
  if (!Array.isArray(uiParts)) return [];
  return uiParts.flatMap(part => {
    const parsed = uiToolPartSchema.safeParse(part);
    return parsed.success ? [parsed.data] : [];
  });
}

function getToolPartName(toolPart: { type: string; toolName?: string }): string {
  if (toolPart.toolName) return toolPart.toolName;
  if (toolPart.type === 'dynamic-tool') return '';
  return toolPart.type.slice('tool-'.length);
}

function uniqueSorted(values: string[]): string[] {
  return [...new Set(values)].sort();
}
